// The site's URLs, as app.js and the Worker both read and write them: which
// page a path is, what its query asks for, and the API request each page is
// built from, spelt one way. Nothing here touches the DOM.

import { type AppResponse, type HistoryRange, isHistoryRange } from "../../shared/api.ts";
import { type CompanyRole, companyParts, companySlug, isCompanyRole, isCompanySlug } from "../../shared/companies.ts";
import { appPath, companyPath, searchWords } from "../../shared/site.ts";
import { pageHref } from "./markup.ts";
import { TAG_PAGES, type TagPageName, type TagPageRef, isTagPageName, tagPageValue } from "./tag-pages.ts";

export const SITE = "https://steamdata.dev";

// Games per list page; a game's rank says which page it's on.
export const PER_PAGE = 50;

// The API requests a list page is built from, spelt one way: they're also the
// keys the Worker embeds its responses under for app.js to find (and
// steam-api's cache keys, where another spelling would be another entry).
export const listPath = (page: number): string => `/api/apps?page=${page}&per_page=${PER_PAGE}`;
export const moversPath = (limit: number): string => `/api/movers?limit=${limit}`;
// How many games each card on /tags shows.
export const TAGS_PREVIEW_GAMES = 5;

export const MOVERS_PATH = moversPath(TAGS_PREVIEW_GAMES);

// The API request for a company page, spelt one way (see listPath).
export const companyApiPath = (role: CompanyRole, slug: string, page: number): string =>
  `/api/companies/${role}/${encodeURIComponent(slug)}?limit=${PER_PAGE}&page=${page}`;

// The API request for a tag page, spelt one way (see listPath).
export const tagApiPath = (tag: TagPageName, value: string, page: number, limit = PER_PAGE): string =>
  `/api/tags/${tag}?${TAG_PAGES[tag].param}=${value}&limit=${limit}&page=${page}`;

// A list page's ?page, as app.js and the Worker both read it: a plain number
// up to 999 (room for ~50,000 ranked games), else page 0. The cap bounds the
// distinct pages anyone can make the Worker ask steam-api for, each a cache
// miss and a D1 query the first time.
export function listPage(search: string | URLSearchParams): number {
  const requested = new URLSearchParams(search).get("page") ?? "";
  return /^(0|[1-9]\d{0,2})$/.test(requested) ? Number(requested) : 0;
}

// Whether a paged list's response (/api/apps, /api/tags/<tag>) has anything on
// it: a page past the end is a later page with no rows. Page 0 is always
// found, to say the list is empty.
export const listFound = (list: { apps: readonly unknown[]; page: number }): boolean =>
  list.apps.length > 0 || list.page === 0;

// ---- Search -------------------------------------------------------------

// The search page's ?q, as app.js and the Worker both read it: trimmed, with
// runs of spaces made one, and no longer than the search box allows, since
// it's written back into the page.
const searchText = (search: string | URLSearchParams): string =>
  (new URLSearchParams(search).get("q") ?? "").trim().replace(/\s+/g, " ").slice(0, 100);

// A query's words (searchWords) as the API reads them, space-separated, or
// "" when they have too little to search by: under 2 letters or digits, which
// the API ignores.
function searchName(q: string): string {
  const words = searchWords(q);
  return words.join("").length < 2 ? "" : words.join(" ");
}

// The search page: games by name and by tag, any of TAG_PAGES' tags and
// values. The header's search box lands on it; not indexed.
export const SEARCH_PATH = "/search";

// A search's query, as app.js and the Worker both read it from the URL: `q`
// as searchText reads it, `tags` as { tag: [values] } of TAG_PAGES' tags and
// values only, each in TAG_PAGES' order (?trend=rising&busy_hours=europe,
// a param repeated for more values), and `page`, as listPage reads it.
export interface SearchQuery {
  q: string;
  tags: Record<string, string[]>;
  page: number;
}

export function searchQuery(search: string | URLSearchParams): SearchQuery {
  const params = new URLSearchParams(search);
  const tags: Record<string, string[]> = {};

  for (const [tag, { values }] of Object.entries(TAG_PAGES)) {
    const asked = params.getAll(tag);
    const known = Object.keys(values).filter((value) => asked.includes(value));
    if (known.length) tags[tag] = known;
  }

  return { q: searchText(search), tags, page: listPage(search) };
}

// A search form's fields (the search page's) as its query. The form has no
// file inputs, so every value is text.
export function searchFormQuery(data: FormData): SearchQuery {
  const params = new URLSearchParams();
  for (const [name, value] of data) {
    if (typeof value === "string") params.append(name, value);
  }
  return searchQuery(params);
}

// A search's params, spelt one way: q, then each tag's values.
function searchParams(q: string, tags: Record<string, string[]>): URLSearchParams {
  const params = new URLSearchParams(q ? { q } : {});
  for (const [tag, values] of Object.entries(tags)) {
    for (const value of values) params.append(tag, value);
  }
  return params;
}

// Games a search page shows: as many as the header's search always did, as
// most searches are for one game by name.
const SEARCH_PER_PAGE = 25;

// The search page's URL for a query (searchQuery's), at `page`.
export function searchHref({ q, tags }: Omit<SearchQuery, "page">, page = 0): string {
  const params = searchParams(q, tags).toString();
  return pageHref(page, params ? `${SEARCH_PATH}?${params}` : SEARCH_PATH);
}

// The API request for a search's page, by the name's words (searchName) and
// its tags, or null when it has neither to search by. A name with too little
// to search by is dropped, as the API would. Also the key the Worker embeds
// the response under.
export function searchPath(
  { q, tags }: Omit<SearchQuery, "page">,
  page: number,
  limit = SEARCH_PER_PAGE
): string | null {
  const words = searchName(q);
  if (!words && !Object.keys(tags).length) return null;

  const params = searchParams(words, tags);
  params.set("limit", String(limit));
  params.set("page", String(page));
  return `/api/search?${params}`;
}

// ---- Game pages ---------------------------------------------------------

// A game page's path: /apps/:id, with the id spelt one way and no longer than
// Steam's (32-bit) ids run. /apps/0730 would be the same game under another
// key in steam-api's cache, so anyone could pad their way to a D1 query per
// request; the Worker redirects it instead (appPathFix).
export const APP_PATH = /^\/apps\/([1-9]\d{0,9})\/?$/;

// The game page another spelling under /apps/ means: the id unpadded, without
// anything after it (/apps/0730, /apps/730/Counter-Strike), as Steam's own
// /app/:id/<name> is redirected (public/_redirects). null for a path with no
// such id, which is not found.
export function appPathFix(pathname: string): string | null {
  const match = pathname.match(/^\/apps\/0*([1-9]\d{0,9})(?:\/.*)?$/);
  return match?.[1] ? appPath(match[1]) : null;
}

// The chart's range a game page opens on.
const DEFAULT_RANGE: HistoryRange = "7d";

// A game page's ?range, as app.js and the Worker both read it.
export function pageRange(search: string): HistoryRange {
  const requested = new URLSearchParams(search).get("range");
  return isHistoryRange(requested) ? requested : DEFAULT_RANGE;
}

// The embeddable card (embed.html, embed.ts): a game's count, change and last
// 7 days, for other sites' iframes.
export const EMBED_PATH = "/embed";

// ---- Steam's URLs -------------------------------------------------------

// The Steam post an update was announced in.
export const postUrl = (appId: number, update: { gid: string }): string =>
  `https://store.steampowered.com/news/app/${appId}/view/${update.gid}`;

// A game's Steam store page.
export const storeUrl = (appId: string | number): string =>
  `https://store.steampowered.com/app/${appId}/`;

// ---- Tags ---------------------------------------------------------------

// The page a path is: { tag, value, def } (def being its TAG_PAGES value),
// or null for none.
export function tagPageOf(pathname: string): TagPageRef | null {
  const [, tag, value] = pathname.match(/^\/tags\/([a-z_]{1,40})\/([a-z_]{1,40})\/?$/) ?? [];
  // Own keys only, so /tags/constructor/length is no page.
  if (!isTagPageName(tag) || value === undefined) return null;
  const def = tagPageValue(tag, value);
  return def ? { tag, value, def } : null;
}

// ---- Compare ------------------------------------------------------------

// The compare page: up to MAX_COMPARE games' counts on one chart, the games
// and range in its URL (/compare?apps=730,570&range=30d). Drawn by app.js
// alone (compare.ts); the Worker serves it index.html, and it isn't indexed.
export const COMPARE_PATH = "/compare";
export const MAX_COMPARE = 4;

interface CompareQuery {
  ids: number[];
  range: HistoryRange;
}

// A compare page's games, as ?apps lists them (app ids, comma-separated):
// each id once, in order, the first MAX_COMPARE, anything else skipped; and
// its range, as a game page's.
export function compareQuery(search: string | URLSearchParams): CompareQuery {
  const params = new URLSearchParams(search);
  const ids: number[] = [];

  for (const part of (params.get("apps") ?? "").split(",")) {
    const id = part.trim();
    if (!/^[1-9]\d{0,9}$/.test(id) || ids.length >= MAX_COMPARE) continue;
    if (!ids.includes(Number(id))) ids.push(Number(id));
  }
  return { ids, range: pageRange(params.toString()) };
}

// The compare page's URL for games `ids` over `range` (the default range
// left out).
export function compareHref(ids: readonly number[], range: HistoryRange = DEFAULT_RANGE): string {
  const params: string[] = [];
  if (ids.length) params.push(`apps=${ids.join(",")}`);
  if (range !== DEFAULT_RANGE) params.push(`range=${range}`);
  return params.length ? `${COMPARE_PATH}?${params.join("&")}` : COMPARE_PATH;
}

// The games a game page offers to compare it with: those ranked just above
// and below it (its `nearby`), none when it isn't ranked.
export const compareNeighbours = (game: Pick<AppResponse, "rank" | "nearby">): AppResponse["nearby"] => {
  const { rank } = game;
  return rank == null
    ? []
    : (game.nearby ?? []).filter((row) => row.rank === rank - 1 || row.rank === rank + 1);
};

// A game page's way to the compare page: the game and its neighbours, over
// the range its chart shows.
export function compareWith(
  game: Pick<AppResponse, "app_id" | "rank" | "nearby">,
  range: HistoryRange = DEFAULT_RANGE
): string {
  return compareHref([game.app_id, ...compareNeighbours(game).map((row) => row.app_id)], range);
}

// ---- Companies ----------------------------------------------------------

// A company's page: its role and its slug as companySlug spells it.
export interface CompanyPageRef {
  role: CompanyRole;
  slug: string;
}

// What a path under a company role (/developer/…, /publisher/…) is: `page`,
// { role, slug }, for a slug as companySlug spells it; else `fix`, the page
// another spelling means (/publisher/Electronic Arts, /developer/CAPCOM), for
// a redirect; neither for nothing. null for a path under no role.
export function companyRoute(
  pathname: string
): { page: CompanyPageRef | null; fix: string | null } | null {
  const [, role, rest = ""] = pathname.match(/^\/([a-z]+)\/(.*)$/) ?? [];
  if (role === undefined || !isCompanyRole(role)) return null;

  const parts = rest.match(/^([^/]+)\/?$/) ? companyParts(role, rest.replace(/\/$/, "")) : null;
  if (!parts) return { page: null, fix: null };
  if (isCompanySlug(parts.slug)) return { page: parts, fix: null };

  const slug = companySlug(parts.slug);
  return { page: null, fix: slug ? companyPath(role, slug) : null };
}
